import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Table } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx"; // Import xlsx
import axios from 'axios'

// Custom styles for DatePicker
const datePickerStyles = `
  .react-datepicker-wrapper {
    width: 100%;
  }
  .react-datepicker__input-container input {
    width: 100% !important;
    height: 48px !important;
    border-radius: 10px !important;
    border: 2px solid #e9ecef !important;
    padding: 12px 14px !important;
    font-size: 0.9rem !important;
    background-color: #ffffff !important;
    transition: all 0.3s ease !important;
  }
  .react-datepicker__input-container input:focus {
    border-color: #28a745 !important;
    box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25) !important;
    outline: none !important;
  }
  .react-datepicker__input-container input::placeholder {
    color: #6c757d;
    font-style: italic;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = datePickerStyles;
  document.head.appendChild(styleSheet);
}

const Report = () => {
  const [reportData, setReportData] = useState([]); // Holds API data
  const [filteredData, setFilteredData] = useState([]); // Data filtered by user input
  const [tenants, setTenants] = useState([]); // List of tenants
  const [report, setReport] = useState(""); // Selected report type
  const [formValues, setFormValues] = useState({
    fromDate: null,
    toDate: null,
    tenantId: "",
    vehicleType: "All", // Default to "All"
  });
  const [isSubmitted, setIsSubmitted] = useState(false); // Submission status

  // Fetch tenants (offices)
  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found, please log in.");
        return;
      }
      const response = await axios.get("https://housys.skymarkone.in:8001/api/getoffice", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data && response.data.office) {
        setTenants(response.data.office); // Update tenants state with office data
      } else {
        console.error("Error fetching offices:", response.data.msg);
      }
    } catch (error) {
      console.error("Error fetching tenants:", error.response?.data || error.message);
    }
  };

  // Fetch entry-exit data
  const fetchData = async () => {
    const token = localStorage.getItem("token");
    if (!formValues.tenantId || !formValues.fromDate || !formValues.toDate) {
      console.error("Please select all the required fields.");
      return;
    }

    try {
      const formatDate = (date) => {
        if (!date) return null;
        const d = new Date(date);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
          d.getDate()
        ).padStart(2, "0")}`;
      };

      const payload = {
        office_id: formValues.tenantId, // Send the selected tenant (office) ID
        vehicleType: formValues.vehicleType, // Add vehicle type filter
        tenant_id: formValues.tenantId,
        report,
        start_date: formatDate(formValues.fromDate),
        end_date: formatDate(formValues.toDate),
      };

      const response = await axios.post(
        "https://housys.skymarkone.in:8001/api/get-entry-exit-data",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      console.log(response.data.data, "Response Data");

      if (response.data.status === "success") {
        setReportData(response.data.data);
      } else {
        console.error("Unexpected response format or status:", response.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // Handle form changes
  const handleDateChange = (date, name) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: date,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangereport = (e) => {
    setReport(e.target.value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    fetchData();
  };

  // Export to Excel
  const exportToExcel = () => {
    const formatDateTime = (datetime) => {
      if (!datetime) return "N/A";
      // Parse the ISO string directly without timezone conversion
      const isoString = datetime;
      const datePart = isoString.split('T')[0];
      const timePart = isoString.split('T')[1].split('.')[0];
      const [year, month, day] = datePart.split('-');
      const [hours24, minutes, seconds] = timePart.split(':');
      
      let hours = parseInt(hours24);
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;
      const hoursStr = String(hours).padStart(2, '0');
      return `${day}/${month}/${year}, ${hoursStr}:${minutes}:${seconds} ${ampm}`;
    };

    const columns = ["EN ID", "User Name", "Office Name", "Vehicle No.", "Vehicle Type", "Entry Time", "Exit Time"];
    const rows = filteredData.map((data) => [
      data.en_id,
      data.User?.name || "N/A",
      data.Office?.company_name || "N/A",
      data.Vehicle.vehicle_number || "N/A",
      data.Vehicle.vehicle_type || "N/A",
      formatDateTime(data.entry_datetime),
      formatDateTime(data.exits_timedate),
    ]);

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet([columns, ...rows]);
    
    // Define styles for headers
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "4472C4" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };
    
    // Define styles for data cells
    const dataStyle = {
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "CCCCCC" } },
        bottom: { style: "thin", color: { rgb: "CCCCCC" } },
        left: { style: "thin", color: { rgb: "CCCCCC" } },
        right: { style: "thin", color: { rgb: "CCCCCC" } }
      }
    };

    // Apply styles to headers (first row)
    columns.forEach((_, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: colIndex });
      if (!ws[cellAddress]) ws[cellAddress] = {};
      ws[cellAddress].s = headerStyle;
    });

    // Apply styles to data cells
    rows.forEach((row, rowIndex) => {
      row.forEach((_, colIndex) => {
        const cellAddress = XLSX.utils.encode_cell({ r: rowIndex + 1, c: colIndex });
        if (!ws[cellAddress]) ws[cellAddress] = {};
        ws[cellAddress].s = dataStyle;
      });
    });

    // Set column widths
    ws['!cols'] = [
      { wch: 10 }, // EN ID
      { wch: 20 }, // User Name
      { wch: 25 }, // Office Name
      { wch: 15 }, // Vehicle No.
      { wch: 12 }, // Vehicle Type
      { wch: 25 }, // Entry Time
      { wch: 25 }  // Exit Time
    ];

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Entry Exit Report");

    // Generate filename with current date and time
    const now = new Date();
    const dateTime = now.toISOString().slice(0, 19).replace(/[:]/g, '-').replace('T', '_');
    const selectedTenant = tenants.find(tenant => tenant.office_id === formValues.tenantId);
    const tenantName = selectedTenant ? selectedTenant.office_name.replace(/[^a-zA-Z0-9]/g, '_') : 'All_Tenants';
    const reportType = report ? report.replace(/\s+/g, '_') : 'All_Report';
    const fileName = `${reportType}_${tenantName}_${dateTime}.xlsx`;

    const excelFile = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    // Save the file using file-saver
    saveAs(new Blob([excelFile], { type: "application/octet-stream" }), fileName);
  };

  // Use backend-filtered data as-is. Client-side date filtering is commented out
  // because the backend already applies date/time filters.
  useEffect(() => {
    setFilteredData(reportData);

    // Previous client-side filtering (kept for reference):
    /*
    setFilteredData(
      reportData.filter((item) => {
        if (!formValues.fromDate || !formValues.toDate) return true;

        const entryDate = item.entry_datetime ? new Date(item.entry_datetime) : null;
        const exitDate = item.exits_timedate ? new Date(item.exits_timedate) : null;
        console.log("Exit Date: ", exitDate);

        // Set time to start and end of day for proper comparison
        const from = new Date(formValues.fromDate);
        from.setHours(0, 0, 0, 0); // Start of from date (00:00:00)
        
        const to = new Date(formValues.toDate);
        to.setHours(23, 59, 59, 999); // End of to date (23:59:59.999)

        return (
          (!entryDate || (entryDate >= from && entryDate <= to)) &&
          (!exitDate || (exitDate >= from && exitDate <= to))
        );
      })
    );
    */
  }, [reportData]);

  useEffect(() => {
    fetchTenants();
  }, []);


  return (
    <Container fluid className="p-4" style={{ 
      minHeight: "100vh", 
    }}>
      {/* Header Section */}
      <div className="mb-4">
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center mb-3">
            <div 
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "15px",
                background: "linear-gradient(135deg, #17a2b8 0%, #138496 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: "15px"
              }}
            >
              <i className="bi bi-graph-up text-white" style={{ fontSize: "24px" }}></i>
            </div>
            <div>
              <h2 style={{ 
                color: "#212529", 
                fontWeight: "700",
                fontSize: "1.75rem",
                marginBottom: "5px"
              }}>
                Entry Tickets History Reports
              </h2>
              <p style={{ 
                color: "#6c757d", 
                marginBottom: "0",
                fontSize: "1rem"
              }}>
                Generate comprehensive entry and exit reports with advanced filtering
              </p>
            </div>
          </div>
          <Button
            style={{
              background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
              border: "none",
              borderRadius: "12px",
              padding: "12px 24px",
              fontWeight: "600",
              boxShadow: "0 4px 15px rgba(40, 167, 69, 0.3)",
              transition: "all 0.3s ease"
            }}
            onClick={exportToExcel}
            disabled={filteredData.length === 0}
            onMouseEnter={(e) => {
              if (!e.target.disabled) {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 6px 20px rgba(40, 167, 69, 0.4)";
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 15px rgba(40, 167, 69, 0.3)";
            }}
          >
            <i className="bi bi-download me-2"></i>
            Export to Excel
            {filteredData.length > 0 && (
              <span className="ms-2 badge bg-light text-dark">
                {filteredData.length} records
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Filter Form Card */}
      <div className="card mb-4" style={{
        border: "none",
        borderRadius: "20px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        background: "#ffffff"
      }}>
        <div className="card-header" style={{
          background: "#ffffff",
          borderBottom: "2px solid #dee2e6",
          borderRadius: "20px 20px 0 0",
          padding: "20px 25px"
        }}>
          <div className="d-flex align-items-center">
            <i className="bi bi-funnel me-3 text-primary" style={{ fontSize: "20px" }}></i>
            <strong style={{ 
              fontSize: "1.1rem", 
              color: "#495057",
              fontWeight: "700"
            }}>
              Report Filters
            </strong>
          </div>
        </div>
        <div className="card-body" style={{ padding: "30px" }}>
          <Form onSubmit={handleSubmit}>
            {/* Single Row - All Filters */}
            <Row className="align-items-end mb-3">
              <Col lg={2} md={6} className="mb-3">
                <Form.Group controlId="selectReport">
                  <Form.Label style={{
                    fontWeight: "600",
                    color: "#495057",
                    marginBottom: "8px",
                    display: "block",
                    fontSize: "0.9rem"
                  }}>
                    <i className="bi bi-file-earmark-text me-1 text-info"></i>
                    Report Type
                  </Form.Label>
                  <Form.Control
                    as="select"
                    name="report"
                    value={report}
                    onChange={handleChangereport}
                    style={{
                      borderRadius: "10px",
                      border: "2px solid #e9ecef",
                      padding: "12px 14px",
                      fontSize: "0.9rem",
                      transition: "all 0.3s ease",
                      height: "48px",
                      backgroundColor: "#ffffff"
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#17a2b8";
                      e.target.style.boxShadow = "0 0 0 0.2rem rgba(23, 162, 184, 0.25)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e9ecef";
                      e.target.style.boxShadow = "none";
                    }}
                  >
                    <option value="">Choose type...</option>
                    <option value="Entry Report">📥 Entry</option>
                    <option value="Exits Report">📤 Exits</option>
                    <option value="All Report">📊 All</option>
                  </Form.Control>
                </Form.Group>
              </Col>

              <Col lg={3} md={6} className="mb-3">
                <Form.Group controlId="selectTenant">
                  <Form.Label style={{
                    fontWeight: "600",
                    color: "#495057",
                    marginBottom: "8px",
                    display: "block",
                    fontSize: "0.9rem"
                  }}>
                    <i className="bi bi-building me-1 text-primary"></i>
                    Select Tenant
                  </Form.Label>
                  <Form.Control
                    as="select"
                    name="tenantId"
                    value={formValues.tenantId}
                    onChange={handleChange}
                    style={{
                      borderRadius: "10px",
                      border: "2px solid #e9ecef",
                      padding: "12px 14px",
                      fontSize: "0.9rem",
                      transition: "all 0.3s ease",
                      height: "48px",
                      backgroundColor: "#ffffff"
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#664CBE";
                      e.target.style.boxShadow = "0 0 0 0.2rem rgba(102, 76, 190, 0.25)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e9ecef";
                      e.target.style.boxShadow = "none";
                    }}
                  >
                    <option value="">Choose tenant...</option>
                    {tenants.map((tenant) => (
                      <option key={tenant.office_id} value={tenant.office_id}>
                        {tenant.office_name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>

              <Col lg={2} md={6} className="mb-3">
                <Form.Group controlId="selectVehicleType">
                  <Form.Label style={{
                    fontWeight: "600",
                    color: "#495057",
                    marginBottom: "8px",
                    display: "block",
                    fontSize: "0.9rem"
                  }}>
                    <i className="bi bi-car-front me-1 text-warning"></i>
                    Vehicle Type
                  </Form.Label>
                  <Form.Control
                    as="select"
                    name="vehicleType"
                    value={formValues.vehicleType}
                    onChange={handleChange}
                    style={{
                      borderRadius: "10px",
                      border: "2px solid #e9ecef",
                      padding: "12px 14px",
                      fontSize: "0.9rem",
                      transition: "all 0.3s ease",
                      height: "48px",
                      backgroundColor: "#ffffff"
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#ffc107";
                      e.target.style.boxShadow = "0 0 0 0.2rem rgba(255, 193, 7, 0.25)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e9ecef";
                      e.target.style.boxShadow = "none";
                    }}
                  >
                    <option value="All">🚗 All Vehicles</option>
                    <option value="4W">🚗 4 Wheeler (Car)</option>
                    <option value="2W">🏍️ 2 Wheeler (Bike)</option>
                  </Form.Control>
                </Form.Group>
              </Col>

              <Col lg={2} md={6} className="mb-3">
                <Form.Group controlId="fromDate">
                  <Form.Label style={{
                    fontWeight: "600",
                    color: "#495057",
                    marginBottom: "8px",
                    display: "block",
                    fontSize: "0.9rem"
                  }}>
                    <i className="bi bi-calendar-date me-1 text-success"></i>
                    From Date
                  </Form.Label>
                  <div style={{ position: "relative" }}>
                    <DatePicker
                      selected={formValues.fromDate || null}
                      onChange={(date) => handleDateChange(date, "fromDate")}
                      className="form-control"
                      dateFormat="yyyy-MM-dd"
                      placeholderText="Start date"
                      style={{
                        borderRadius: "10px",
                        border: "2px solid #e9ecef",
                        padding: "12px 14px",
                        fontSize: "0.9rem",
                        height: "48px",
                        width: "100%",
                        backgroundColor: "#ffffff"
                      }}
                    />
                    <i 
                      className="bi bi-calendar3 position-absolute text-muted" 
                      style={{
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                        fontSize: "1rem"
                      }}
                    ></i>
                  </div>
                </Form.Group>
              </Col>

              <Col lg={2} md={6} className="mb-3">
                <Form.Group controlId="toDate">
                  <Form.Label style={{
                    fontWeight: "600",
                    color: "#495057",
                    marginBottom: "8px",
                    display: "block",
                    fontSize: "0.9rem"
                  }}>
                    <i className="bi bi-calendar-check me-1 text-warning"></i>
                    To Date
                  </Form.Label>
                  <div style={{ position: "relative" }}>
                    <DatePicker
                      selected={formValues.toDate || null}
                      onChange={(date) => handleDateChange(date, "toDate")}
                      className="form-control"
                      dateFormat="yyyy-MM-dd"
                      placeholderText="End date"
                      style={{
                        borderRadius: "10px",
                        border: "2px solid #e9ecef",
                        padding: "12px 14px",
                        fontSize: "0.9rem",
                        height: "48px",
                        width: "100%",
                        backgroundColor: "#ffffff"
                      }}
                    />
                    <i 
                      className="bi bi-calendar3 position-absolute text-muted" 
                      style={{
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                        fontSize: "1rem"
                      }}
                    ></i>
                  </div>
                </Form.Group>
              </Col>

              <Col lg={2} md={12} className="mb-3">
                <Button 
                  type="submit"
                  style={{
                    background: "linear-gradient(135deg, #664CBE 0%, #4472C4 100%)",
                    border: "none",
                    borderRadius: "10px",
                    padding: "12px 24px",
                    fontWeight: "600",
                    width: "100%",
                    height: "48px",
                    transition: "all 0.3s ease",
                    boxShadow: "0 2px 8px rgba(102, 76, 190, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.95rem"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = "translateY(-1px)";
                    e.target.style.boxShadow = "0 4px 12px rgba(102, 76, 190, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "0 2px 8px rgba(102, 76, 190, 0.2)";
                  }}
                >
                  <i className="bi bi-play-fill me-2" style={{ fontSize: "1rem" }}></i>
                  Generate Report
                </Button>
              </Col>
            </Row>
          </Form>
        </div>
      </div>

      {/* Results Section */}
      <div className="card" style={{
        border: "none",
        borderRadius: "20px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        background: "#ffffff"
      }}>
        {isSubmitted && filteredData.length === 0 ? (
          <div className="card-body text-center py-5">
            <div style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px"
            }}>
              <i className="bi bi-exclamation-triangle text-white" style={{ fontSize: "32px" }}></i>
            </div>
            <h4 style={{ color: "#495057", marginBottom: "10px" }}>No Data Available</h4>
            <p style={{ color: "#6c757d", fontSize: "1rem" }}>
              No data found for the selected filters. Please adjust your criteria and try again.
            </p>
          </div>
        ) : (
          filteredData.length > 0 && (
            <>
              <div className="card-header" style={{
                background: "#ffffff",
                borderBottom: "2px solid #dee2e6",
                borderRadius: "20px 20px 0 0",
                padding: "20px 25px"
              }}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-table me-3 text-primary" style={{ fontSize: "20px" }}></i>
                    <strong style={{ 
                      fontSize: "1.1rem", 
                      color: "#495057",
                      fontWeight: "700"
                    }}>
                      Report Results
                    </strong>
                  </div>
                  <span className="badge" style={{
                    background: "linear-gradient(135deg, #17a2b8 0%, #138496 100%)",
                    color: "white",
                    padding: "8px 16px",
                    borderRadius: "20px",
                    fontSize: "0.9rem",
                    fontWeight: "600"
                  }}>
                    {filteredData.length} Records Found
                  </span>
                </div>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <Table hover style={{ marginBottom: "0" }}>
                    <thead>
                      <tr style={{ background: "#f8f9fa" }}>
                        <th style={{
                          padding: "16px 20px",
                          fontWeight: "700",
                          color: "#495057",
                          borderBottom: "2px solid #dee2e6",
                          fontSize: "0.95rem"
                        }}>
                          <i className="bi bi-hash me-2 text-primary"></i>
                          EN ID
                        </th>
                        <th style={{
                          padding: "16px 20px",
                          fontWeight: "700",
                          color: "#495057",
                          borderBottom: "2px solid #dee2e6",
                          fontSize: "0.95rem"
                        }}>
                          <i className="bi bi-person me-2 text-success"></i>
                          User Name
                        </th>
                        <th style={{
                          padding: "16px 20px",
                          fontWeight: "700",
                          color: "#495057",
                          borderBottom: "2px solid #dee2e6",
                          fontSize: "0.95rem"
                        }}>
                          <i className="bi bi-building me-2 text-info"></i>
                          Office Name
                        </th>
                        <th style={{
                          padding: "16px 20px",
                          fontWeight: "700",
                          color: "#495057",
                          borderBottom: "2px solid #dee2e6",
                          fontSize: "0.95rem"
                        }}>
                          <i className="bi bi-car-front me-2 text-warning"></i>
                          Vehicle No.
                        </th>
                        <th style={{
                          padding: "16px 20px",
                          fontWeight: "700",
                          color: "#495057",
                          borderBottom: "2px solid #dee2e6",
                          fontSize: "0.95rem"
                        }}>
                          <i className="bi bi-gear me-2 text-secondary"></i>
                          Vehicle Type
                        </th>
                        <th style={{
                          padding: "16px 20px",
                          fontWeight: "700",
                          color: "#495057",
                          borderBottom: "2px solid #dee2e6",
                          fontSize: "0.95rem"
                        }}>
                          <i className="bi bi-box-arrow-in-right me-2 text-success"></i>
                          Entry Time
                        </th>
                        <th style={{
                          padding: "16px 20px",
                          fontWeight: "700",
                          color: "#495057",
                          borderBottom: "2px solid #dee2e6",
                          fontSize: "0.95rem"
                        }}>
                          <i className="bi bi-box-arrow-right me-2 text-danger"></i>
                          Exit Time
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((item, index) => (
                        <tr 
                          key={index}
                          style={{
                            transition: "all 0.2s ease",
                            borderBottom: "1px solid #f1f3f4"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#f8f9fa";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                          }}
                        >
                          <td style={{ 
                            padding: "16px 20px", 
                            fontWeight: "600",
                            color: "#495057"
                          }}>
                            <span className="badge bg-light text-dark" style={{
                              padding: "6px 12px",
                              borderRadius: "10px",
                              fontSize: "0.85rem"
                            }}>
                              {item.en_id}
                            </span>
                          </td>
                          <td style={{ 
                            padding: "16px 20px", 
                            color: "#495057",
                            fontWeight: "500"
                          }}>
                            {item.User?.name || (
                              <span className="text-muted" style={{ fontStyle: "italic" }}>
                                N/A
                              </span>
                            )}
                          </td>
                          <td style={{ 
                            padding: "16px 20px", 
                            color: "#495057"
                          }}>
                            {item.Office?.company_name ? (
                              <span className="badge" style={{
                                background: "linear-gradient(135deg, #17a2b8 0%, #138496 100%)",
                                color: "white",
                                padding: "6px 12px",
                                borderRadius: "10px",
                                fontSize: "0.8rem"
                              }}>
                                {item.Office.company_name}
                              </span>
                            ) : (
                              <span className="text-muted" style={{ fontStyle: "italic" }}>
                                N/A
                              </span>
                            )}
                          </td>
                          <td style={{ 
                            padding: "16px 20px", 
                            color: "#495057",
                            fontWeight: "600"
                          }}>
                            {item.Vehicle.vehicle_number || (
                              <span className="text-muted" style={{ fontStyle: "italic" }}>
                                N/A
                              </span>
                            )}
                          </td>
                          <td style={{ 
                            padding: "16px 20px", 
                            color: "#495057"
                          }}>
                            {item.Vehicle.vehicle_type ? (
                              <span className={`badge ${item.Vehicle.vehicle_type === '4W' ? 'bg-primary' : 'bg-success'}`} style={{
                                padding: "6px 12px",
                                borderRadius: "10px",
                                fontSize: "0.8rem"
                              }}>
                                {item.Vehicle.vehicle_type === '4W' ? '🚗 4W' : '🏍️ 2W'}
                              </span>
                            ) : (
                              <span className="text-muted" style={{ fontStyle: "italic" }}>
                                N/A
                              </span>
                            )}
                          </td>
                          <td style={{ 
                            padding: "16px 20px", 
                            color: "#495057",
                            fontSize: "0.9rem"
                          }}>
                            {item.entry_datetime 
                              ? (() => {
                                  // Parse the ISO string directly without timezone conversion
                                  const isoString = item.entry_datetime;
                                  const datePart = isoString.split('T')[0];
                                  const timePart = isoString.split('T')[1].split('.')[0];
                                  const [year, month, day] = datePart.split('-');
                                  const [hours24, minutes, seconds] = timePart.split(':');
                                  
                                  let hours = parseInt(hours24);
                                  const ampm = hours >= 12 ? 'PM' : 'AM';
                                  hours = hours % 12;
                                  hours = hours ? hours : 12;
                                  const hoursStr = String(hours).padStart(2, '0');
                                  return (
                                    <div>
                                      <div style={{ fontWeight: "600", color: "#28a745" }}>
                                        {`${day}/${month}/${year}`}
                                      </div>
                                      <small style={{ color: "#6c757d" }}>
                                        {`${hoursStr}:${minutes}:${seconds} ${ampm}`}
                                      </small>
                                    </div>
                                  );
                                })()
                              : <span className="text-muted" style={{ fontStyle: "italic" }}>N/A</span>
                            }
                          </td>
                          <td style={{ 
                            padding: "16px 20px", 
                            color: "#495057",
                            fontSize: "0.9rem"
                          }}>
                            {item.exits_timedate 
                              ? (() => {
                                  // Parse the ISO string directly without timezone conversion
                                  const isoString = item.exits_timedate;
                                  const datePart = isoString.split('T')[0];
                                  const timePart = isoString.split('T')[1].split('.')[0];
                                  const [year, month, day] = datePart.split('-');
                                  const [hours24, minutes, seconds] = timePart.split(':');
                                  
                                  let hours = parseInt(hours24);
                                  const ampm = hours >= 12 ? 'PM' : 'AM';
                                  hours = hours % 12;
                                  hours = hours ? hours : 12;
                                  const hoursStr = String(hours).padStart(2, '0');
                                  return (
                                    <div>
                                      <div style={{ fontWeight: "600", color: "#dc3545" }}>
                                        {`${day}/${month}/${year}`}
                                      </div>
                                      <small style={{ color: "#6c757d" }}>
                                        {`${hoursStr}:${minutes}:${seconds} ${ampm}`}
                                      </small>
                                    </div>
                                  );
                                })()
                              : <span className="text-muted" style={{ fontStyle: "italic" }}>N/A</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            </>
          )
        )}
      </div>
    </Container>
  );
};

export default Report;
